import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dashApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatDakar } from "@/lib/time";
import {
  ShieldAlert, ArrowLeft, ScrollText, Loader2, Search, RefreshCw, Shield, UserCog, Globe,
} from "lucide-react";
import { toast } from "sonner";

const ROLE_FILTERS = [
  { k: "all", label: "Tous" },
  { k: "admin", label: "Admins" },
  { k: "manager", label: "Managers" },
];

const Activity = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("all");

  const load = async () => {
    setLoading(true);
    try { setLogs(await dashApi.activity()); }
    catch { toast.error("Impossible de charger le journal d'activité"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = logs;
    if (role !== "all") list = list.filter((l) => l.role === role);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((l) =>
        (l.user_name || "").toLowerCase().includes(q) ||
        (l.action || "").toLowerCase().includes(q) ||
        (l.detail || "").toLowerCase().includes(q) ||
        (l.ip || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [logs, role, search]);

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100">
      <header className="h-16 sticky top-0 z-40 backdrop-blur-md bg-slate-900/80 border-b border-slate-800 px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-950 border border-emerald-500/30 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="font-display font-bold leading-tight flex items-center gap-2"><ScrollText className="w-4 h-4 text-yellow-400" /> Journal d'activité</h1>
            <p className="text-[11px] font-mono text-slate-500">Sama Jaamu — Audit des connexions & actions</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="activity-refresh-btn" onClick={load} size="icon" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button data-testid="activity-back-btn" onClick={() => navigate("/")} variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800">
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input data-testid="activity-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher par utilisateur, action, détail, IP..." className="pl-9 bg-slate-900 border-slate-700 text-slate-100 h-10" />
          </div>
          <div className="flex gap-1.5">
            {ROLE_FILTERS.map((f) => (
              <button key={f.k} data-testid={`activity-filter-${f.k}`} onClick={() => setRole(f.k)}
                className={`px-4 text-xs font-medium rounded-lg border transition-all ${role === f.k ? "bg-emerald-600 border-emerald-500 text-white" : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-200"}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-semibold">Événements</h2>
          <span className="font-mono text-xs text-slate-500" data-testid="activity-count">{filtered.length} entrée(s)</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40 text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="bg-slate-900/60 border border-dashed border-slate-700 rounded-2xl p-10 text-center text-slate-500">
            <ScrollText className="w-8 h-8 mx-auto mb-3 text-slate-600" />
            <p className="font-display font-semibold text-slate-300">Aucune activité</p>
            <p className="text-sm mt-1">Les connexions et actions apparaîtront ici.</p>
          </div>
        ) : (
          <div className="bg-slate-900/60 border border-slate-800 rounded-2xl overflow-hidden" data-testid="activity-table">
            <div className="hidden md:grid grid-cols-[180px_1fr_1fr_120px] gap-3 px-4 py-2.5 border-b border-slate-800 text-[10px] font-mono uppercase tracking-wider text-slate-500">
              <span>Date (Dakar)</span><span>Utilisateur</span><span>Action</span><span>IP</span>
            </div>
            <div className="divide-y divide-slate-800/70">
              {filtered.map((l) => (
                <div key={l.id} data-testid={`activity-row-${l.id}`} className="grid grid-cols-1 md:grid-cols-[180px_1fr_1fr_120px] gap-1 md:gap-3 px-4 py-3 hover:bg-slate-800/30">
                  <span className="text-xs font-mono text-slate-400">{formatDakar(l.created_at)}</span>
                  <span className="flex items-center gap-1.5 text-sm text-slate-200 min-w-0">
                    {l.role === "admin" ? <Shield className="w-3.5 h-3.5 text-yellow-400 shrink-0" /> : l.role === "manager" ? <UserCog className="w-3.5 h-3.5 text-blue-400 shrink-0" /> : <Globe className="w-3.5 h-3.5 text-slate-500 shrink-0" />}
                    <span className="truncate">{l.user_name}</span>
                  </span>
                  <span className="text-sm min-w-0"><span className="text-emerald-300 font-medium">{l.action}</span>{l.detail ? <span className="text-slate-500"> — {l.detail}</span> : null}</span>
                  <span className="text-xs font-mono text-slate-500">{l.ip || "—"}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Activity;
